import { type Deal, PIPELINE_STATUS_COLORS } from '@/types';
import { PIPELINE_STATUS_ORDER } from '@arcanum-hq/shared-types';
import { formatCompactCurrency } from '@/lib/format';
import { capitalWeightedIrr } from '@/lib/pipeline-irr';
import { StatusProgression } from './StatusProgression';

// C3: local parseBasis DELETED — use canonical capitalWeightedIrr from
// lib/pipeline-irr.ts (§13.C single-source rule). parseBasisUsd handles
// B/M/K suffixes + Number.isFinite guard; the old local parseBasis lacked B-suffix.

interface PipelineSummaryProps {
  deals: Deal[];
  activeStatus: string | null;
  onStatusClick: (status: string | null) => void;
  onStatusHover: (status: string | null) => void;
  statusFilter: string | null;
}

export function PipelineSummary({
  deals,
  activeStatus,
  onStatusClick,
  onStatusHover,
  statusFilter,
}: PipelineSummaryProps) {
  // C3: denominator = full `deals` (cap-bar % + BY STATUS counts reflect the
  // WHOLE pipeline per Lovable reference). Status counts always from full set.
  const statusCounts: Record<string, number> = {};
  PIPELINE_STATUS_ORDER.forEach((s) => {
    statusCounts[s] = deals.filter((d) => d.status === s).length;
  });

  // Aggregates over the full deals array (no local re-filter —
  // route already passes versionGatedDeals as `deals`; C2 ensures
  // the route sends the full set to Summary and filtered set to table).
  const totalCap = deals.reduce((sum, d) => {
    if (!d.basis) return sum;
    // inline inline-parse to avoid importing parseBasisUsd just for totalCap
    // (capitalWeightedIrr already imports it internally).
    const stripped = d.basis.replace(/[$,\s]/g, '');
    const suffix = stripped.slice(-1).toUpperCase();
    const num =
      suffix === 'M' || suffix === 'B' || suffix === 'K'
        ? parseFloat(stripped.slice(0, -1))
        : parseFloat(stripped);
    if (!Number.isFinite(num)) return sum;
    if (suffix === 'B') return sum + num * 1e9;
    if (suffix === 'M') return sum + num * 1e6;
    if (suffix === 'K') return sum + num * 1e3;
    return sum + num;
  }, 0);

  const totalUnits = deals.reduce((sum, d) => sum + (d.units ?? 0), 0);
  const dealCount = deals.length;

  // C3: Pipeline IRR — canonical capitalWeightedIrr (§13.C; no local parseBasis).
  const pipelineIrr = capitalWeightedIrr(
    deals.map((d) => ({ irr: d.irr ?? null, basis: d.basis ?? null }))
  );

  return (
    <div className="w-[298px] min-w-[298px] bg-panel-bg border border-panel-border rounded-[3px] flex flex-col overflow-hidden">
      {/* Header */}
      <div className="px-3 py-2 border-b border-panel-border shrink-0 flex items-center justify-between">
        {/* C3: SUMMARY title 9px (was text-xs = 12px) */}
        <span className="text-[9px] font-mono font-semibold text-text-muted tracking-[1.4px]">
          SUMMARY
        </span>
        {activeStatus && (
          <span
            className="text-[7px] font-mono px-1.5 py-px rounded-[2px]"
            style={{
              color:
                PIPELINE_STATUS_COLORS[
                  activeStatus as keyof typeof PIPELINE_STATUS_COLORS
                ],
              background: `color-mix(in srgb, ${
                PIPELINE_STATUS_COLORS[
                  activeStatus as keyof typeof PIPELINE_STATUS_COLORS
                ]
              } 8%, transparent)`,
              border: `1px solid color-mix(in srgb, ${
                PIPELINE_STATUS_COLORS[
                  activeStatus as keyof typeof PIPELINE_STATUS_COLORS
                ]
              } 13%, transparent)`,
            }}
          >
            {activeStatus.toUpperCase()}
          </span>
        )}
      </div>

      <div className="px-3 py-2 flex-1 flex flex-col gap-2 min-h-0">
        {/* Total Capitalization big number */}
        <div className="shrink-0">
          <div className="text-[9px] text-text-dim font-mono tracking-[0.9px] mb-[2px]">
            TOTAL CAPITALIZATION
          </div>
          <div className="text-[20px] text-text-bright font-mono font-bold tracking-[-0.5px] leading-none tabular-nums">
            {totalCap > 0 ? formatCompactCurrency(totalCap) : dealCount}
          </div>
        </div>

        {/* C3: always-shown 3-tile row — UNITS / DEALS / PIPELINE IRR */}
        <div className="flex gap-[14px] justify-center shrink-0">
          <div className="text-center">
            <div className="text-[9px] text-text-dim font-mono tracking-[0.9px] mb-[2px]">
              UNITS
            </div>
            <div className="text-[15px] text-text-bright font-mono font-semibold leading-none tabular-nums">
              {totalUnits.toLocaleString()}
            </div>
          </div>
          <div className="text-center">
            <div className="text-[9px] text-text-dim font-mono tracking-[0.9px] mb-[2px]">
              DEALS
            </div>
            <div className="text-[15px] text-text-bright font-mono font-semibold leading-none tabular-nums">
              {dealCount}
            </div>
          </div>
          <div className="text-center">
            <div className="text-[9px] text-text-dim font-mono tracking-[0.9px] mb-[2px]">
              PIPELINE IRR
            </div>
            {/* C3: IRR tile in accent color (Lovable PipelinePage.tsx:99) */}
            <div className="text-[15px] text-accent font-mono font-semibold leading-none tabular-nums">
              {pipelineIrr}
            </div>
          </div>
        </div>

        <div className="h-px bg-panel-border shrink-0" />

        {/* Status breakdown — flex-1 + min-h-0 so StatusProgression can scroll
            internally on short viewports while KPI block above stays visible */}
        <div className="flex-1 min-h-0 pb-2">
          <StatusProgression
            statuses={PIPELINE_STATUS_ORDER}
            statusCounts={statusCounts}
            activeStatus={activeStatus}
            onStatusClick={onStatusClick}
            onStatusHover={onStatusHover}
            statusFilter={statusFilter}
            totalDeals={deals.length}
          />
        </div>
      </div>
    </div>
  );
}
